import { useEffect, useState } from "react";
import { Check, Circle } from "lucide-react";
import { api } from "@/lib/api";

const FALLBACK_POLICY = { min_length: 12, max_length: 128 };

export function usePasswordPolicy() {
  const [policy, setPolicy] = useState(FALLBACK_POLICY);
  useEffect(() => {
    api
      .get("/auth/password-policy")
      .then(({ data }) => setPolicy(data))
      .catch(() => setPolicy(FALLBACK_POLICY));
  }, []);
  return policy;
}

export function evaluatePassword(password, email, policy) {
  const local = (email || "").split("@")[0].toLowerCase();
  const parts = local.split(/[.\-_+]/).filter((p) => p.length >= 4);
  const lower = password.toLowerCase().replace(/\s/g, "");
  return [
    { id: "length", label: `At least ${policy.min_length} characters`, ok: password.length >= policy.min_length },
    { id: "max", label: `No more than ${policy.max_length} characters`, ok: password.length > 0 && password.length <= policy.max_length },
    { id: "trim", label: "No leading or trailing spaces", ok: password.length > 0 && password.trim() === password },
    { id: "identity", label: "Does not contain your email or “bizii”", ok: password.length > 0 && !lower.includes("bizii") && !parts.some((p) => lower.includes(p)) },
    { id: "pattern", label: "Not a repeated pattern", ok: password.length > 0 && new Set(password).size > 2 && !/^(.{1,3})\1{3,}$/.test(password) },
  ];
}

export function PasswordPolicyHint({ password, email, policy }) {
  const checks = evaluatePassword(password, email, policy);
  const passed = checks.filter((c) => c.ok).length;
  const strength = Math.min(100, Math.round((Math.min(password.length, 24) / 24) * 60 + (passed / checks.length) * 40));
  return (
    <div data-testid="password-policy-hint" className="space-y-3 rounded-md border border-white/8 bg-graphite-950/60 p-4">
      <div className="flex items-center justify-between">
        <span className="bz-caption">Passphrase readiness</span>
        <span data-testid="password-strength-value" className="font-mono text-xs text-slate-300">
          {password ? `${strength}%` : "—"}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full bg-electric transition-[width] duration-300"
          style={{ width: `${password ? strength : 0}%`, boxShadow: strength > 80 ? "0 0 12px rgba(0,242,253,0.6)" : "none" }}
        />
      </div>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {checks.map((check) => (
          <li key={check.id} data-testid={`password-check-${check.id}`} data-ok={check.ok} className={`flex items-center gap-2 text-xs ${check.ok ? "text-slate-200" : "text-slate-500"}`}>
            {check.ok ? <Check className="h-3.5 w-3.5 text-electric" strokeWidth={2} /> : <Circle className="h-3.5 w-3.5" strokeWidth={1.75} />}
            {check.label}
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">Long passphrases with spaces are encouraged. No forced symbols, no periodic rotation.</p>
    </div>
  );
}
